import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import bg from "../assets/background.jpg";
import { CabinetHeader } from "../components/shared/Header";
import { useAuth } from "../lib/auth";
import { ProjectsTable } from "../components/shared/ProjectsTable";
import { Project } from "../models/project";
import { StatCard, Stat } from "../components/shared/StatCard";
import { createConnectUrl, fetchTelegramAdminSetting, fetchTelegramLink } from "../lib/telegram";
import { getTelegramAppInitData, telegramAppLink } from "../lib/telegramApp";
import {
  fetchDashboardStats,
  fetchOwnedProjects,
  formatMoney,
} from "../lib/projects";
import {
  LuArrowUpRight,
  LuClock,
  LuCoins,
  LuFilePlus2,
  LuLayers,
  LuSend,
  LuSquareCheckBig,
} from "react-icons/lu";

export const Cabinet = () => {
  const { user, profile, loading } = useAuth();
  const firstName = profile?.name?.split(" ")[0];
  const isGuest = !loading && !user;
  const isStaff = profile?.role === "admin" || profile?.role === "manager";
  const [stats, setStats] = useState<Awaited<ReturnType<typeof fetchDashboardStats>> | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [telegramUrl, setTelegramUrl] = useState<string | null>(null);
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [telegramConnected, setTelegramConnected] = useState<boolean | null>(null);

  useEffect(() => {
    if (!user || !profile) return;
    const isStaffRole = profile.role === "admin" || profile.role === "manager";
    const check = isStaffRole
      ? fetchTelegramAdminSetting(profile.id).then((t) => t !== null)
      : fetchTelegramLink().then((t) => t !== null);
    check.then(setTelegramConnected).catch(() => setTelegramConnected(false));
  }, [user, profile]);

  useEffect(() => {
    if (!user) return;
    Promise.all([fetchDashboardStats(), fetchOwnedProjects(5)])
      .then(([s, p]) => {
        setStats(s);
        setProjects(p);
      })
      .catch(() => undefined);
  }, [user]);

  const handleTelegram = async () => {
    if (!user) return;
    setTelegramLoading(true);
    try {
      const initData = getTelegramAppInitData();
      let linked = false;
      if (initData) {
        try {
          await telegramAppLink(initData);
          linked = true;
        } catch {
        }
      }
      if (linked) {
        setTelegramConnected(true);
      } else {
        setTelegramUrl(await createConnectUrl());
      }
    } catch {
      setTelegramUrl("https://t.me/NeoteploPartnerBot");
    } finally {
      setTelegramLoading(false);
    }
  };

  const onConnectClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (telegramConnected) return;
    if (!telegramUrl) {
      e.preventDefault();
      handleTelegram();
    }
  };

  const statCards: Stat[] = stats
    ? [
        { icon: LuLayers, label: "Проєктів в роботі", value: String(stats.inProgress), unit: "шт." },
        { icon: LuSquareCheckBig, label: "Реалізовано проєктів", value: String(stats.completed), unit: "шт." },
        { icon: LuClock, label: "На погодженні", value: String(stats.approval), unit: "шт." },
        isStaff
          ? { icon: LuCoins, label: "Нараховано бонусів", value: formatMoney(stats.bonusTotal), unit: "" }
          : { icon: LuCoins, label: "Доступні бонуси", value: formatMoney(stats.bonusTotal), unit: "" },
      ]
    : [];

  return (
    <div className="flex-1 flex min-h-screen flex-col bg-night">
      <CabinetHeader container />

      <section
        className="flex flex-1 items-center bg-cover bg-center"
        style={{ backgroundImage: `linear-gradient(to right, rgba(10,10,10,0.92) 40%, rgba(10,10,10,0.4)), url(${bg})` }}
      >
        <div className="mx-auto w-full max-w-cabinet px-5 py-12 md:py-16">
          <h1 className="text-4xl font-bold text-white md:text-5xl">
            {firstName ? `Вітаємо, ${firstName}!` : "Вітаємо!"}
          </h1>
          <p className="mt-3 text-xl text-neutral-300">
            {isGuest
              ? "Платформа для архітекторів та дизайнерів"
              : isStaff ? "Ваш кабінет адміністратора" : "Ваш кабінет архітектора"}
          </p>
          <p className="mt-7 max-w-md text-neutral-400">
            Передавайте проєкти, відстежуйте статуси, отримуйте бонуси та
            розвивайте партнерство з IDM.
          </p>
          <div className="mt-10 flex flex-col items-start gap-4 md:flex-row md:items-center md:gap-6">
            {isGuest ? (
              <>
                <Link to="/signup" className="btn-brand">
                  <LuFilePlus2 className="text-xl" aria-hidden />
                  Зареєструватися
                </Link>
                <p className="max-w-[220px] text-sm text-neutral-300">
                  Створіть акаунт, щоб передавати проєкти в роботу
                </p>
              </>
            ) : (
              <>
                <Link to={isStaff ? "/dashboard" : "/submit"} className="btn-brand">
                  <LuFilePlus2 className="text-xl" aria-hidden />
                  {isStaff ? "Дивитись проєкти" : "Передати новий проєкт"}
                </Link>
                {!isStaff && (
                  <p className="max-w-[220px] text-sm text-neutral-300">
                    Заповніть форму та передайте новий проєкт в роботу
                  </p>
                )}               
              </>
            )}
          </div>
        </div>
      </section>

      {isGuest ? null : (
      <main className="mx-auto w-full max-w-cabinet px-5 pt-8 pb-12">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {statCards.map((s) => (
            <StatCard key={s.label} {...s} />
          ))}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <section className="card relative overflow-hidden p-6 md:p-8">
            <h2 className="text-xl font-bold text-white md:mt-2">Ваші бонуси</h2>
            <p className="mt-4 text-4xl font-bold text-brand">
              {stats ? formatMoney(stats.bonusTotal).replace(" €", "") : "—"} <span className="text-lg font-normal">€</span>
            </p>
            {/* <p className="mt-3 text-sm text-neutral-400">
              Доступно для виведення
            </p> */}
            {/* <Link to="/dashboard" className="btn-outline-brand mt-3 md:mt-7">
              Вивести бонуси
            </Link> */}
            <LuCoins
              className="absolute -bottom-5 right-7 text-[140px] text-white/10"
              aria-hidden
            />
          </section>

          <section className="card p-4 sm:p-6 md:p-8">
            <div className="flex items-start gap-4">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand">
                <LuSend className="text-2xl text-black" aria-hidden />
              </span>
              <div>
                <h2 className="text-xl font-bold text-white">
                  Telegram бот для архітекторів
                </h2>
                <p className="mt-1 text-sm text-neutral-400">
                  Сповіщення, оновлення та багато корисного.
                </p>
              </div>
            </div>
            {user ? (
              <a
                href={telegramConnected
                  ? "https://t.me/NeoteploPartnerBot"
                  : telegramUrl ?? "https://t.me/NeoteploPartnerBot"}
                target="_blank"
                rel="noreferrer"
                onClick={onConnectClick}
                className={`btn-outline-brand items-end mt-3 md:mt-5 ${telegramLoading ? "opacity-50 pointer-events-none" : ""}`}
              >
                <span>{telegramConnected
                  ? "Перейти в Telegram"
                  : telegramLoading
                    ? "Підключення…"
                    : "Підключити Telegram"}
                </span>
                <LuArrowUpRight className="text-lg" aria-hidden />
              </a>
            ) : (
              <a
                href="https://t.me/NeoteploPartnerBot"
                target="_blank"
                rel="noreferrer"
                className="btn-outline-brand mt-3 md:mt-7"
              >
                Перейти в Telegram <LuArrowUpRight aria-hidden />
              </a>
            )}
          </section>
        </div>

        {projects.length > 0 && <ProjectsTable projects={projects} />}
      </main>
      )}
    </div>
  );
};